import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Zap,
  Package,
  Layers,
  Printer,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Warehouse as WarehouseIcon,
  Tag,
  Calendar,
  X,
  Sparkles,
} from 'lucide-react';
import { trpcClient } from '@/lib/trpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatNumber } from '@/lib/utils';
import { format } from 'date-fns';
import { warehouseDomainOf } from '@apv-erp/status';
import {
  MaterialStagingPrintTemplate,
  type MaterialStagingPrintData,
} from './print/MaterialStagingPrintTemplate';

interface ShopfloorMaterialStagingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productionOrderId: string;
  poCode: string;
  productName: string;
  customerName?: string;
  materialId: string;
  materialCode: string;
  materialName: string;
  unit?: string;
  qtyRequired: number;
  qtyIssued?: number;
  materialPlanId?: string;
  routingStepId?: string;
  routingSteps?: Array<{ id: string; name: string; stepOrder: number }>;
  onSuccess?: () => void;
}

export function ShopfloorMaterialStagingDialog({
  open,
  onOpenChange,
  productionOrderId,
  poCode,
  productName,
  customerName,
  materialId,
  materialCode,
  materialName,
  unit = 'tờ',
  qtyRequired,
  qtyIssued = 0,
  materialPlanId,
  routingStepId,
  routingSteps = [],
  onSuccess,
}: ShopfloorMaterialStagingDialogProps) {
  const queryClient = useQueryClient();
  const printRef = useRef<HTMLDivElement>(null);

  // Remaining needed quantity
  const remainingQty = Math.max(0, qtyRequired - qtyIssued);

  // Target routing step selection
  const [selectedRoutingStepId, setSelectedRoutingStepId] = useState<string>(routingStepId || '');
  const [notes, setNotes] = useState<string>('');

  // Warehouses query
  const { data: rawWarehouses } = useQuery({
    queryKey: ['warehouses', 'list'],
    queryFn: () => trpcClient.warehouses.list.query() as any,
    enabled: open,
  });
  // Chỉ cấp vật tư từ kho thuộc nhóm Kho vật tư (trước đây rơi về kho đầu danh sách → kho kỹ thuật / thành phẩm)
  const warehouses = (rawWarehouses?.items || rawWarehouses || []).filter(
    (w: any) => warehouseDomainOf(w.type) === 'MATERIAL',
  );

  // Kho mặc định: KHO-VT, không có thì kho vật tư đầu tiên
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('');
  useEffect(() => {
    if (warehouses.length > 0 && !selectedWarehouseId) {
      const vt = warehouses.find((w: any) => w.code === 'KHO-VT');
      setSelectedWarehouseId(vt ? vt.id : warehouses[0].id);
    }
  }, [warehouses, selectedWarehouseId]);

  // Set default routing step if not set
  useEffect(() => {
    if (!selectedRoutingStepId && routingSteps.length > 0) {
      // Find In Offset or first step
      const printStep = routingSteps.find((s) => s.name?.toLowerCase().includes('in'));
      setSelectedRoutingStepId(printStep ? printStep.id : routingSteps[0].id);
    }
  }, [routingSteps, selectedRoutingStepId]);

  // Kho quản lý theo tổng số lượng: chỉ cần tồn khả dụng của vật tư trong kho (không chọn lô)
  const { data: rawLots, isLoading: isLoadingLots } = useQuery({
    queryKey: ['inventory', 'getAvailableLotsForMaterial', { materialId, warehouseId: selectedWarehouseId }],
    queryFn: () =>
      trpcClient.inventory.getAvailableLotsForMaterial.query({
        materialId,
        warehouseId: selectedWarehouseId,
      }),
    enabled: open && !!materialId && !!selectedWarehouseId,
  });
  const stockRows: any[] = Array.isArray(rawLots) ? rawLots : (rawLots?.lots || []);
  const stockAvailable = stockRows.reduce((sum: number, r: any) => sum + Number(r.qtyAvailable || 0), 0);

  // Số lượng cấp lần này: mặc định phần còn thiếu, không vượt tồn
  const [issueQty, setIssueQty] = useState<number>(0);
  useEffect(() => {
    if (open) setIssueQty(Math.min(remainingQty, stockAvailable));
  }, [open, materialId, selectedWarehouseId, remainingQty, stockAvailable]);

  const totalAllocated = Number(issueQty) || 0;
  const isSatisfied = totalAllocated >= remainingQty;

  // Staging Mutation
  const stageMutation = useMutation({
    mutationFn: (input: any) => trpcClient.materialPlanning.stageMaterialToShopfloor.mutate(input),
    onSuccess: (res) => {
      toast.success(`Cấp phát thành công ${res.totalSupplied} ${unit}! Phiếu: ${res.docNo}`);

      // Prepare print data
      const targetStep = routingSteps.find((s) => s.id === selectedRoutingStepId);
      const wh = warehouses.find((w: any) => w.id === selectedWarehouseId);

      const lotsToPrint = [{ lotNumber: '', quantity: res.totalSupplied, locationCode: wh?.code || '', expiryDate: undefined }];

      setPrintData({
        docNo: res.docNo,
        docDate: new Date(),
        poCode,
        productName,
        customerName,
        materialCode,
        materialName,
        unit,
        targetStepName: targetStep?.name || 'In Offset',
        warehouseName: wh?.name || 'Kho Vật Tư',
        notes,
        lots: lotsToPrint,
        totalQuantity: res.totalSupplied,
      });

      setShowPrintDialog(true);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['production-order'] });
      queryClient.invalidateQueries({ queryKey: ['material-planning'] });
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });

      if (onSuccess) onSuccess();
    },
    onError: (err: any) => {
      toast.error(err.message || 'Lỗi khi cấp phát vật tư sàn xưởng');
    },
  });

  const handleConfirmStage = () => {
    if (totalAllocated <= 0) {
      toast.error('Nhập số lượng cấp phát');
      return;
    }
    if (totalAllocated > stockAvailable) {
      toast.error(`Kho chỉ còn ${formatNumber(stockAvailable)} ${unit}`);
      return;
    }
    stageMutation.mutate({
      productionOrderId,
      materialPlanId,
      materialId,
      warehouseId: selectedWarehouseId,
      quantity: totalAllocated,
      routingStepId: selectedRoutingStepId || undefined,
      notes: notes || `Cấp vật tư cho LSX ${poCode}`,
    });
  };

  // Print Dialog State
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printMode, setPrintMode] = useState<'DOCUMENT' | 'PALLET_DECAL'>('DOCUMENT');
  const [printData, setPrintData] = useState<MaterialStagingPrintData | null>(null);

  const handlePrint = () => {
    const printEl = printRef.current;
    if (!printEl) return;
    const printWindow = window.open('', '_blank', 'width=1100,height=850');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${printMode === 'PALLET_DECAL' ? 'THE_PALLET' : 'PHIEU_CAP_PHAT'}_${poCode}</title>
          <style>
            @page {
              size: ${printMode === 'PALLET_DECAL' ? 'A5 landscape' : 'A4 portrait'};
              margin: 10mm;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              margin: 0;
              padding: 0;
              color: #000;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          </style>
          <script src="https://cdn.tailwindcss.com"></script>
        </head>
        <body>
          ${printEl.innerHTML}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          {/* Header */}
          <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white border-b border-indigo-800">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-indigo-500/20 text-indigo-300 rounded-lg border border-indigo-400/30">
                    <Package className="h-5 w-5" />
                  </span>
                  <DialogTitle className="text-xl font-bold tracking-tight text-white">
                    Cấp Vật Tư
                  </DialogTitle>
                </div>
                <DialogDescription className="sr-only">Cấp vật tư từ kho ra sàn máy cho lệnh sản xuất</DialogDescription>
              </div>
            </div>

            {/* Context Breadcrumbs */}
            <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-indigo-800/60 text-xs">
              <div>
                <span className="text-indigo-300 block">Lệnh sản xuất:</span>
                <span className="font-mono font-bold text-white text-sm">{poCode}</span>
              </div>
              <div>
                <span className="text-indigo-300 block">Sản phẩm:</span>
                <span className="font-semibold text-white break-words whitespace-normal block">{productName}</span>
              </div>
              <div>
                <span className="text-indigo-300 block">Vật tư cấp:</span>
                <span className="font-bold text-amber-300 break-words whitespace-normal block">
                  [{materialCode}] {materialName}
                </span>
              </div>
            </div>
          </DialogHeader>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* KPI Ribbon */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <span className="text-[11px] uppercase font-bold text-slate-500 block">Định Mức KHSX</span>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black font-mono text-slate-800">
                    {formatNumber(qtyRequired)}
                  </span>
                  <span className="text-xs font-semibold text-slate-500 uppercase">{unit}</span>
                </div>
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <span className="text-[11px] uppercase font-bold text-slate-500 block">Đã Cấp Trước</span>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black font-mono text-blue-700">
                    {formatNumber(qtyIssued)}
                  </span>
                  <span className="text-xs font-semibold text-slate-500 uppercase">{unit}</span>
                </div>
              </div>

              <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-xl">
                <span className="text-[11px] uppercase font-bold text-amber-800 block">Còn Thiếu Sàn Máy</span>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black font-mono text-amber-700">
                    {formatNumber(remainingQty)}
                  </span>
                  <span className="text-xs font-semibold text-amber-800 uppercase">{unit}</span>
                </div>
              </div>

              <div className={`p-3 border rounded-xl transition-all ${
                isSatisfied
                  ? 'bg-emerald-50/80 border-emerald-300 text-emerald-950'
                  : 'bg-indigo-50/70 border-indigo-200 text-indigo-950'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase font-bold block">
                    {isSatisfied ? '✅ Cấp đủ' : 'Cấp lần này'}
                  </span>
                </div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className={`text-2xl font-black font-mono ${isSatisfied ? 'text-emerald-700' : 'text-indigo-700'}`}>
                    {formatNumber(totalAllocated)}
                  </span>
                  <span className="text-xs font-semibold uppercase">{unit}</span>
                </div>
              </div>
            </div>

            {/* Warehouse and Routing Step Selector */}
            <div className="grid grid-cols-2 gap-4 bg-slate-50/80 p-3.5 rounded-xl border border-slate-200">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <WarehouseIcon className="h-3.5 w-3.5 text-indigo-600" />
                  Kho Xuất Vật Tư
                </Label>
                <Select value={selectedWarehouseId} onValueChange={setSelectedWarehouseId}>
                  <SelectTrigger className="bg-white h-9">
                    <SelectValue placeholder="Chọn kho..." />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w: any) => (
                      <SelectItem key={w.id} value={w.id}>
                        <span className="font-semibold">{w.name}</span> ({w.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-indigo-600" />
                  Công Đoạn Nhận
                </Label>
                <Select value={selectedRoutingStepId} onValueChange={setSelectedRoutingStepId}>
                  <SelectTrigger className="bg-white h-9">
                    <SelectValue placeholder="Chọn công đoạn..." />
                  </SelectTrigger>
                  <SelectContent>
                    {routingSteps.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        Bước {s.stepOrder}: <span className="font-semibold">{s.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Số lượng cấp: theo tổng tồn kho, không chọn lô */}
            <div className="grid grid-cols-2 gap-4 p-3.5 rounded-xl border border-slate-200" data-testid="staging-qty-section">
              <div className="space-y-1">
                <Label className="text-xs font-bold text-slate-700">Tồn Kho Khả Dụng</Label>
                <div className="h-9 flex items-center font-mono font-bold text-slate-800" data-testid="staging-stock-available">
                  {isLoadingLots ? 'Đang tải...' : `${formatNumber(stockAvailable)} ${unit}`}
                </div>
                {!isLoadingLots && stockAvailable <= 0 && (
                  <p className="text-[11px] text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Kho đã chọn hết tồn vật tư này
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-slate-700">SL Cấp Lần Này</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={stockAvailable}
                    value={issueQty || ''}
                    onChange={(e) => setIssueQty(Math.max(0, Number(e.target.value) || 0))}
                    data-testid="staging-qty-input"
                    className="h-9 w-40 text-right font-mono font-bold"
                  />
                  <span className="text-xs text-slate-500 uppercase">{unit}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-xs font-bold text-slate-700">Ghi Chú</Label>
              <Textarea
                placeholder="VD: Cấp phát ưu tiên cho Ca 1 máy In Heidelberg SM-74..."
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="text-xs"
              />
            </div>
          </div>

          {/* Footer Actions */}
          <DialogFooter className="p-4 bg-slate-100 border-t border-slate-200 flex justify-between items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Đóng
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <span className="text-[11px] text-slate-500 block">Tổng lượng xuất:</span>
                <span className="font-mono font-black text-indigo-900 text-base">
                  {formatNumber(totalAllocated)} {unit}
                </span>
              </div>

              <Button
                size="sm"
                onClick={handleConfirmStage}
                disabled={totalAllocated <= 0 || stageMutation.isPending}
                className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-5 shadow-sm flex items-center gap-2"
              >
                {stageMutation.isPending ? (
                  'Đang xử lý...'
                ) : (
                  <>
                    <Package className="h-4 w-4" />
                    📦 Xác Nhận Xuất Cấp Sàn Xưởng
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instant Print Preview Modal */}
      {showPrintDialog && printData && (
        <Dialog
          open={showPrintDialog}
          onOpenChange={(pOpen) => {
            setShowPrintDialog(pOpen);
            if (!pOpen) {
              onOpenChange(false);
            }
          }}
        >
          <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col p-0 overflow-hidden">
            <DialogHeader className="p-4 bg-slate-900 text-white flex flex-row items-center justify-between">
              <div>
                <DialogTitle className="text-base font-bold text-white flex items-center gap-2">
                  <Printer className="h-4 w-4 text-amber-400" />
                  In Phiếu Xuất Kho &amp; Thẻ Decal Pallet
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-300">
                  Số phiếu: {printData.docNo} • Tổng cấp: {formatNumber(printData.totalQuantity)} {printData.unit}
                </DialogDescription>
              </div>

              <div className="flex items-center gap-2">
                <div className="bg-slate-800 p-1 rounded-lg flex gap-1 border border-slate-700">
                  <Button
                    size="sm"
                    variant={printMode === 'DOCUMENT' ? 'default' : 'ghost'}
                    onClick={() => setPrintMode('DOCUMENT')}
                    className="h-7 text-xs px-2.5"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    Phiếu Xuất A4
                  </Button>
                  <Button
                    size="sm"
                    variant={printMode === 'PALLET_DECAL' ? 'default' : 'ghost'}
                    onClick={() => setPrintMode('PALLET_DECAL')}
                    className="h-7 text-xs px-2.5"
                  >
                    <Tag className="h-3.5 w-3.5 mr-1" />
                    Tem Pallet Decal
                  </Button>
                </div>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto p-6 bg-slate-100 flex justify-center">
              <div className="shadow-lg bg-white">
                <MaterialStagingPrintTemplate ref={printRef} data={printData} mode={printMode} />
              </div>
            </div>

            <DialogFooter className="p-4 bg-white border-t border-slate-200 flex justify-between items-center sm:justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowPrintDialog(false);
                  onOpenChange(false);
                }}
              >
                Hoàn Tất
              </Button>
              <Button
                size="sm"
                onClick={handlePrint}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-1.5"
              >
                <Printer className="h-4 w-4" />
                In Ngay ({printMode === 'PALLET_DECAL' ? 'Tem Decal' : 'Phiếu A4'})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
